import React, { useState } from 'react';
import { Modal, Spin } from 'antd';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { changePaymentMethod } from '../../../redux/businessAccount/stripeAction';
import { getCustomerDetails } from '../../../redux/users/actionCreator';

const stripePromise = loadStripe(process.env.REACT_APP_Stripe_Key || 'pk_test_placeholder');

const ChangeCardForm = ({ onCancel, onUpdateSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setLoading(true);
        try {
            const cardNumberElement = elements.getElement(CardNumberElement);
            const cardExpiryElement = elements.getElement(CardExpiryElement);
            const cardCvcElement = elements.getElement(CardCvcElement);

            if (!cardNumberElement._complete || !cardExpiryElement._complete || !cardCvcElement._complete) {
                setLoading(false);
                toast.error('Please fill in all the required card fields.');
                return;
            }

            const { paymentMethod, error } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardNumberElement,
            });

            if (error) {
                toast.error(error.message);
                setLoading(false);
                return;
            }

            dispatch(
                changePaymentMethod(
                    paymentMethod.id,
                    (newCardData) => {
                        onUpdateSuccess(newCardData);
                        setLoading(false);
                    },
                    () => setLoading(false)
                )
            );
        } catch (err) {
            console.error(err);
            toast.error('An error occurred while updating card');
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="w-100 p-2">
            <h4 className="poppins fw-600 f-18 lh-24 c-0f151d mb-4">
                Change your payment method
            </h4>
            
            <div className="mb-3">
                <label className="poppins c-667085 f-14 lh-20 mb-2 d-block">
                    Card Number
                </label>
                <div className="position-relative">
                    <CardNumberElement
                        options={{ placeholder: '4242 4242 4242 4242' }}
                        className="w-100 border border-color-c-e1e6ef poppins rounded-5 py-3 px-3 lh-24 f-16 c-0f151d"
                    />
                </div>
            </div>

            <div className="poppins d-flex gap-3 justify-content-between align-items-center mb-4">
                <div className="w-100">
                    <label className="poppins c-667085 f-14 lh-20 mb-2 d-block">
                        Expiry Date
                    </label>
                    <CardExpiryElement className="border border-color-c-e1e6ef py-3 px-3 rounded-5 w-100" />
                </div>
                <div className="w-100">
                    <label className="poppins c-667085 f-14 lh-20 mb-2 d-block">
                        Security Code (CVC)
                    </label>
                    <CardCvcElement className="border border-color-c-e1e6ef py-3 px-3 rounded-5 w-100" />
                </div>
            </div>

            <div className="d-flex gap-3 justify-content-end mt-4">
                <button
                    type="button"
                    className="btn btn-outline poppins px-4 py-2"
                    style={{ fontSize: '14px', height: '40px' }}
                    onClick={onCancel}
                    disabled={loading}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="btn btn-prim poppins px-4 py-2 d-flex align-items-center justify-content-center"
                    style={{ fontSize: '14px', minWidth: '110px', height: '40px' }}
                    disabled={!stripe || loading}
                >
                    {loading ? <Spin size="small" /> : 'Save Card'}
                </button>
            </div>
        </form>
    );
};

const ChangeCardModal = ({ visible, onClose }) => {
    const dispatch = useDispatch();
    
    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            destroyOnClose
            centered
        >
            <Elements stripe={stripePromise}>
                <ChangeCardForm
                    onCancel={onClose}
                    onUpdateSuccess={() => {
                        dispatch(getCustomerDetails());
                        onClose();
                    }}
                />
            </Elements>
        </Modal>
    );
};

export default ChangeCardModal;
